// src/utils/secureFetch.js
/**
 * Secure Fetch with Certificate Pinning
 * 
 * This utility provides a secure fetch implementation with certificate pinning
 * for different environments (web, React Native, Electron).
 */

import PINNED_CERTIFICATES from './pinnedCertificates';

// Track domains with pinning warnings to avoid excessive console messages
const warnedDomains = new Set();

/**
 * Extract domain from URL
 * @param {string} url - URL to extract domain from
 * @returns {string} - Domain name
 */
export const extractDomain = (url) => {
  try {
    return new URL(url).hostname;
  } catch (error) {
    console.error('Invalid URL:', url);
    return '';
  }
};

/**
 * Check if a domain has pinned certificates
 * @param {string} domain - Domain to check
 * @returns {boolean} - Whether domain has pinned certificates
 */
export const isDomainPinned = (domain) => {
  return !!PINNED_CERTIFICATES[domain];
};

/**
 * Log a warning for unpinned domains (only once per domain)
 * @param {string} domain - Domain name
 */
const logUnpinnedWarning = (domain) => {
  if (!warnedDomains.has(domain)) {
    console.warn(`⚠️ Certificate pinning: Domain ${domain} is not in the pinned certificates list.`);
    warnedDomains.add(domain);
  }
};

/**
 * Secure fetch with certificate pinning
 * 
 * @param {string} url - URL to fetch
 * @param {Object} options - Fetch options
 * @returns {Promise<Response>} - Fetch response
 */
const secureFetch = async (url, options = {}) => {
  const domain = extractDomain(url);
  
  // React Native environment detection
  const isReactNative = typeof global !== 'undefined' && 
                        global.navigator && 
                        global.navigator.product === 'ReactNative';
  
  // Electron environment detection
  const isElectron = typeof window !== 'undefined' && 
                     typeof window.process === 'object' && 
                     window.process.type === 'renderer';

  // Development mode check
  if (process.env.NODE_ENV === 'development') {
    if (!isDomainPinned(domain)) {
      logUnpinnedWarning(domain);
    }
    return fetch(url, options);
  }

  // React Native implementation
  if (isReactNative) {
    // This would typically use a library like 'react-native-pinch' or 'react-native-ssl-pinning'
    try {
      // Placeholder for React Native implementation
      console.log(`Secure fetch with certificate pinning for ${domain} in React Native`);
      
      // Example implementation with a hypothetical react-native-ssl-pinning library:
      /*
      if (isDomainPinned(domain)) {
        return sslPinning.fetch(url, {
          ...options,
          sslPinning: {
            certs: PINNED_CERTIFICATES[domain]
          }
        });
      } else {
        logUnpinnedWarning(domain);
        return fetch(url, options);
      }
      */
      
      // For now, fallback to regular fetch
      return fetch(url, options);
    } catch (error) {
      console.error(`Secure fetch error for ${url}:`, error);
      throw error;
    }
  }

  // Electron implementation
  if (isElectron) {
    // In Electron, we can use the net module with certificate verification
    try {
      console.log(`Secure fetch with certificate pinning for ${domain} in Electron`);
      
      // Example implementation for Electron:
      /*
      const { net } = window.require('electron');
      
      return new Promise((resolve, reject) => {
        const request = net.request({
          method: options.method || 'GET',
          url,
          // Other options...
        });

        request.on('response', (response) => {
          // Check certificate against pinned certificates
          const certificate = response.certificate;
          const fingerprint = certificate.fingerprint;
          
          if (isDomainPinned(domain) && 
              !PINNED_CERTIFICATES[domain].includes(fingerprint)) {
            reject(new Error('Certificate pinning validation failed'));
            return;
          }
          
          // Process response...
          // ...
          
          resolve(response);
        });

        request.on('error', (error) => {
          reject(error);
        });

        request.end();
      });
      */
      
      // For now, fallback to regular fetch
      return fetch(url, options);
    } catch (error) {
      console.error(`Secure fetch error for ${url}:`, error);
      throw error;
    }
  }

  // Web browser implementation
  if (!isDomainPinned(domain)) {
    logUnpinnedWarning(domain);
  }
  
  // For web browsers, we can't enforce certificate pinning directly
  // We rely on the browser's certificate validation and HTTPS
  return fetch(url, options);
};

export default secureFetch;